<script lang="ts" setup>
import PageHeader from '@/components/header/PageHeader.vue'
import { api } from '@/services/ApiService'
import { camelize, validateComponent, ValidatorType } from '@/helper/ValidateDirective'
import bus from '@/helper/Bus'
import { BusEvent, StringMap } from '@/helper/Types'
import { computed, nextTick, onMounted, onUnmounted, reactive, ref, Ref } from 'vue'
import FormElement from '@/components/FormElement.vue'
import { ModalTypes } from '@/helper/ModalTypes'
import SectionIntersectionView from '@/helper/SectionIntersectionView'
import { NavigationGuardNext, onBeforeRouteLeave, RouteLocationNormalized, useRoute } from 'vue-router'
import { removeEmptyValuesFromObject, setMetaTitle } from '@/helper/tools'

const translation = ref<Record<string, any>>({})
const refTranslation = ref<Record<string, any>>({})
const flatTranslation = ref<Record<string, any>>({})

const originalTranslationStr = ref('')
const filterValue = ref('')
let intersector: SectionIntersectionView // Reactive?

const route = useRoute()

const locale = ref(route.params.locale.toString() || '')
const brand = ref(route.params.brand.toString() || '')

const validator: ValidatorType = reactive({})

defineExpose({
  validator
})

const filterUntranslated = ref(false)
const config = ref<StringMap>({}) // Reactive?

const REF_LOCALE = 'en_GB'
const REF_BRAND = 'vaillant'
const NAMESPACE = 'addform'
const EDITABLE_KEY_PREFIXES = [
  //Key prefixes of items that are independent of the reference translations
  'report.projectInfoPage.flowTemperatures'
]

const KEY_HAS_CONFIG_REF: Record<string, () => string[]> = {
  'step_heating_details.flow_temperatures': () => {
    const flowTemps = {} as StringMap
    const systemTypes = config.value.systemTypes as StringMap[]

    systemTypes.forEach(sysType => {
      const mergedFlowTemperatures = [
        ...sysType.heatingFlowTemperatures,
        ...(sysType.coolingFlowTemperature ? [sysType.coolingFlowTemperature] : [])
      ]
      mergedFlowTemperatures.forEach(temp => flowTemps[temp] = 1)
    })

    return Object.keys(flowTemps).map(temp => `DEG_${normalizeKey(temp)}`)
  },
  'step_heating_details.ground_types': () => {
    const groundTypes = config.value.groundTypes as StringMap[]
    return groundTypes?.map(g => normalizeKey(g.name as string)) || []
  },
  'step_heating_details.dhw_amount_labels': () =>
    ((config?.value.dhwValues || []) as string[]).map(temp => `AMOUNT_${normalizeKey(temp)}`),
  'step_heating_details.years_of_construction': () =>
    ((config?.value.constructionYears || []) as StringMap[]).map(year => year.key),
  'step_heating_details.window_types': () =>
    ((config?.value.heatingLoadCalculation?.windowTypes || []) as StringMap[]).map(window => window.windowType)
}

function normalizeKey(key: string): string {
  return (key || '').toString().replace(/\./g, '_')
}

const sections = computed<string[]>(() =>
  Object.keys(refTranslation.value)
    .filter(key => Object.keys(flatTranslation.value[key] || {}).length)
    .sort()
)

const newKey = ref('')
const newValue = ref('')

const COMPONENT_NAME = 'TranslationEditPage'
bus.$onC(COMPONENT_NAME, BusEvent.TRANSLATION_DELETE_CONFIRMED, onTranslationDelete)
setMetaTitle(`Admin config - Translation for ${brand.value} / ${locale.value}`)

onBeforeRouteLeave((to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext) => {
  let allowNav = true
  if (hasChanges()) {
    allowNav = confirm('There are UNSAVED changes. Leave page?')
  }
  next(allowNav)
})

onMounted(async (): Promise<void> => {
  await loadData()
  intersector = new SectionIntersectionView(sections.value)

  window.addEventListener('beforeunload', reloadHandler)
})

onUnmounted((): void => {
  window.removeEventListener('beforeunload', reloadHandler)
})

function hasChanges(): boolean {
  return originalTranslationStr.value !== JSON.stringify(translation.value)
}

function reloadHandler(e: Event): void {
  e = e || window.event
  ;(document.activeElement as HTMLElement).blur()
  if (hasChanges()) {
    e.preventDefault()
    e.returnValue = false
  }
}

/**
 * Checks if the currently selected translation is the reference translation (en_GB)
 */
const isReference = computed<boolean>(() => brand.value.toLowerCase() === REF_BRAND && locale.value === REF_LOCALE)

/**
 * Loads the translation data
 */
async function loadData(): Promise<void> {
  config.value = await api.loadAdminConfig(brand.value, locale.value.toUpperCase().split('_')[1])
  translation.value = await api.loadAdminTranslations(brand.value, locale.value)
  refTranslation.value = isReference.value
    ? translation.value
    : await api.loadAdminTranslations(REF_BRAND.toUpperCase(), REF_LOCALE)
  flattenTranslation()
  originalTranslationStr.value = JSON.stringify(translation.value)

  // fix initially the textareas
  resizeAllTextareas()
}

function resizeAllTextareas() {
  setTimeout(() => {
    document.querySelectorAll('textarea').forEach(resizeTextarea)
  }, 100)
}

/**
 * Recursively adds config keys object structure to the translation object, so it can be filled with the values
 * Returns the lowest level
 */
function createConfigRefKey(ref: StringMap, keyArray: string[]): StringMap {
  const currentKey = keyArray.shift()
  if (!currentKey) {
    return {}
  }
  if (keyArray.length > 0) {
    if (!ref[currentKey]) {
      ref[currentKey] = {}
    }
    return createConfigRefKey(ref[currentKey], keyArray)
  }
  if (!ref[currentKey]) {
    ref[currentKey] = {}
  }
  return ref[currentKey]
}

/**
 * Creates a flattend translation object with first level for section and the second for the actual translations
 * key pairs. Makes it easier to handle the translation with the binding inside the template
 */
function flattenTranslation() {
  // collect all section s

  Object.keys(KEY_HAS_CONFIG_REF).forEach(key => {
    // Creates the empty structure and returns the lowest item
    const ref = createConfigRefKey(translation.value, key.split('.'))
    const mergedRefObj: StringMap = {}
    const refKeys = KEY_HAS_CONFIG_REF[key]()
    //In case there are deprecated translations (config items got deleted)
    //the actual config keys get merged with the translation keys (mergedRefObj)
    const mergedKeys = [...refKeys, ...Object.keys(ref)]

    mergedKeys.forEach(key => (mergedRefObj[key] = 1))

    Object.keys(mergedRefObj).forEach(val => {
      if (refKeys.includes(val))
        //Fills newly created translation items with empty string
      {
        ref[val] = ref[val] ? ref[val] : ''
      }//Deletes translations items due to deleted config items
      else {
        delete ref[val]
      }
    })
  })
  const allSections: Record<string, boolean> = {}
  Object.keys(refTranslation.value)
    .concat(Object.keys(translation.value))
    .forEach(k => (allSections[k] = true))

  for (const section of Object.keys(allSections)) {
    const sectionRefTrans = flattenObject(refTranslation.value[section])
    if (undefined === translation.value[section]) {
      //Create the section in translation if not yet existing
      translation.value[section] = {}
    }
    const sectionTrans = flattenObject(translation.value[section])

    // enhance translations with reference keys, but not in reference
    let mergedSectionKeys = Object.keys(sectionTrans)
    if (!isReference.value) {
      // delete editable keys and config ref keys
      Object.keys(sectionRefTrans)
        .filter(k =>
          [...EDITABLE_KEY_PREFIXES, ...Object.keys(KEY_HAS_CONFIG_REF)].some(ek => `${section}.${k}`.indexOf(ek) === 0)
        )
        .forEach(k => delete sectionRefTrans[k])

      mergedSectionKeys = Object.keys(Object.assign({}, sectionTrans, sectionRefTrans))
    }

    const res: Record<string, any> = {}
    mergedSectionKeys.sort((a, b) => (a.toLowerCase() < b.toLowerCase() ? -1 : 1))
    mergedSectionKeys.forEach(key => {
      const keySegments = key.split('.')
      const transKey = keySegments.pop() || '' //returns only the key of the lowest element of the nested object
      let transObj = translation.value[section] //Reference to parent element inside this.translation
      transObj = transObj === undefined ? {} : transObj
      keySegments.forEach(key => {
        //Get the parent object of the key to hold the reference to this.translation
        if (transObj[key] === undefined) {
          transObj[key] = {}
        }
        transObj = transObj[key]
      })

      if (filterUntranslated.value && transObj[transKey]) {
        return
      }

      //Applies the search filter
      const filterLower = filterValue.value.toLowerCase()
      if (
        filterValue.value &&
        !`${section}.${key}`.toLowerCase().includes(filterLower) &&
        !(sectionTrans[key] || '').toLowerCase()?.includes(filterLower) &&
        !(sectionRefTrans[key] || '').toLowerCase().includes(filterLower)
      ) {
        return
      }

      if (transObj[transKey] === undefined) {
        transObj[transKey] = ''
      }

      res[key] = {
        transObj: transObj, //transObj is the object reference to the parent of the lowest key (transKey)
        transKey: transKey, //Lowest key of the nested object
        ref: sectionRefTrans[key] || ''
      }
    })
    flatTranslation.value[section] = res
  }
}

function updateTranslation(): void {
  flattenTranslation()
  resizeAllTextareas()
  setTimeout(() => {
    intersector.reset(sections.value)
  }, 100)
}

/**
 * Flattens a nested Object into Record
 */
function flattenObject(obj: Record<string, any>, prefix = ''): Record<string, string> {
  let res: Record<string, string> = {}

  if (!obj) {
    return {}
  }
  for (const item of Object.keys(obj)) {
    const val = obj[item]
    const key = prefix ? `${prefix}.${item}` : item
    if (typeof val === 'object') {
      res = { ...res, ...flattenObject(val, key) }
      continue
    }
    res[key] = val.toString()
  }
  return res
}

function copyRefText(item: any): void {
  if (item.transObj[item.transKey] === '') {
    item.transObj[item.transKey] = item.ref
  }
}

function sectionNavItemClicked(section: string) {
  document.getElementById(`section-${section}`)?.scrollIntoView({ behavior: 'smooth' })
}

function resizeTextarea(el: HTMLTextAreaElement) {
  el.style.height = 'auto'
  el.style.height = `${el.scrollHeight}px`
}

function getError(key: string): string | Ref<string> {
  return validator[camelize(key)]?.error || ''
}

function addBtnClicked() {
  if (!validateComponent(validator, NAMESPACE)) {
    return
  }

  addNewKey(newKey.value, newValue.value)
  newKey.value = ''
  newValue.value = ''
  updateTranslation()
}

function addNewKey(newKey: string, newValue: string) {
  const keySegments = newKey.split('.')
  const key = keySegments.pop() || ''
  let transObj = translation.value
  keySegments.forEach(s => {
    if (transObj[s] === undefined) {
      transObj[s] = {}
    }
    transObj = transObj[s]
  })
  transObj[key] = newValue
}

function deleteBtnClicked(object: any, key: string, fullkey: string) {
  bus.$emit(BusEvent.SHOW_MODAL, {
    modalType: ModalTypes.GENERAL,
    confirmEventName: BusEvent.TRANSLATION_DELETE_CONFIRMED,
    payload: {
      modalTitle: 'Delete translation',
      modalText: `Are you sure to delete this translation for "${key}"?`,
      confirmButtonText: 'Delete',
      translationObject: object,
      translationKey: key,
      translationFullKey: fullkey
    }
  })
}

function onTranslationDelete(payload: any) {
  newKey.value = payload.translationFullKey
  newValue.value = payload.translationObject[payload.translationKey]
  delete payload.translationObject[payload.translationKey]
  removeEmptyTranslationNodes(translation.value)
  updateTranslation()
}

function convertToKey(...args: string[]) {
  return args.join('.').replace(/\./g, '-')
}

function removeEmptyTranslationNodes(node: any): boolean {
  if (typeof node === 'object') {
    const keys = Object.keys(node)
    if (!keys.length) {
      return false
    }
    keys.forEach(key => {
      if (!removeEmptyTranslationNodes(node[key])) {
        delete node[key]
      }
    })
    return !!Object.keys(node).length
  }
  return node !== undefined && node !== null && node !== ''
}

function resetBtnClicked() {
  loadData()
}

async function saveBtnClicked() {
  if (!validateComponent(validator)) {
    bus.$emit(BusEvent.SCROLL_TO_FIRST_ERROR)
    return
  }

  try {
    await api.updateAdminTranslations(brand.value, locale.value, removeEmptyValuesFromObject(translation.value))
    originalTranslationStr.value = JSON.stringify(translation.value)
    setTimeout(() => {
      alert('Data successfully saved!')
    }, 100)
  } catch {
    alert('Failed to save data! Please check the data.')
  }
}

async function fillEmptyFields(): Promise<void> {
  Object.keys(flatTranslation).forEach(section =>
    Object.keys(flatTranslation.value[section]).forEach(key => {
      const transItem = flatTranslation.value[section][key]
      if (transItem.transObj[transItem.transKey]) {
        return
      }
      transItem.transObj[transItem.transKey] = transItem.ref
    })
  )
  await nextTick()
  validateComponent(validator)
  alert('Empty fields were filled!')
}

async function importClicked() {
  const file = await selectFile()
  if (!file) {
    return
  }
  const csvData = await file?.text()
  parseCsv(csvData)
}

async function dropCsv(ev: DragEvent) {
  if (!ev?.dataTransfer?.files || !ev?.dataTransfer?.files?.length) {
    return
  }
  const csvData = await ev?.dataTransfer?.files[0]?.text()
  parseCsv(csvData)
}

/**
 * Transfers values from CSV file to corresponding inputs
 * Inputs only get filled. The translations form does not get saved
 * Also gets called when Ctrl/Cmd + Shift + 'v' get pressed inside 'key' input field and transfers the passed values
 * from the cache. In this case also new translation input fields get created of not yet existing
 */
function parseCsv(csv: string, sep = ';') {
  const lines = csv.split('\n')
  const maxCols = sep === ':' ? 2 : 3
  const cntFail: string[] = []
  let updateNeeded = false
  let cntSuccess = 0

  lines.forEach(line => {
    const cols = line.split(sep)
    if (cols.length !== maxCols) {
      if (cols.length > 1) {
        cntFail.push(`- ${cols[0]}` + ' (Reason: invalid amount of columns or semicolon used in value)')
      }
      return
    }

    const fullKey = unwrapQuotes(cols[0]).split('.')
    if (fullKey.length < 2) {
      return
    }

    const section = fullKey.shift() || ''
    const key = fullKey.join('.')
    const val = unwrapQuotes(cols[maxCols - 1])

    if (!(section in flatTranslation.value) || !(key in flatTranslation.value[section])) {
      addNewKey(`${section}.${key}`, val)
      updateNeeded = true
      cntSuccess++
    }

    const flatObj = flatTranslation.value[section][key]
    flatObj.transObj[flatObj.transKey] = val

    cntSuccess++
  })
  if (updateNeeded) {
    updateTranslation()
  }
  alert(
    `Import finished.\n\n${cntSuccess} keys imported.\n${cntFail.length} keys failed to import:\n${cntFail.join('\n')}`
  )
}

function unwrapQuotes(val: string): string {
  val = val.trim()
  if (val.substr(0, 1) !== '"' && val.substr(-1) !== '"') {
    return val
  }
  return val.substr(1, val.length - 2).replaceAll('""', '"')
}

function exportClicked() {
  const CRLF = '\r\n'
  const SEP = ';'
  let csv = ''

  csv += `"Key"${SEP}"Reference"${SEP}"${brand.value} / ${locale.value}"${CRLF}`

  for (const section of sections.value) {
    for (const key of Object.keys(flatTranslation.value[section])) {
      const trans = flatTranslation.value[section][key]
      csv += `"${section}.${key}"${SEP}`
      csv += `"${trans.ref.replaceAll('"', '""')}"${SEP}`
      csv += `"${trans.transObj[trans.transKey].replaceAll('"', '""')}"${CRLF}`
    }
  }

  api.downloadBlob(
    [new Uint8Array([0xef, 0xbb, 0xbf]), csv],
    'text/csv; charset=utf-8', //'application/octet-stream',
    `translation-${brand.value.toLowerCase()}-${locale.value.toLowerCase()}.csv`
  )
}

// https://stackoverflow.com/a/52757538/2660387
function selectFile(): Promise<File | null> {
  return new Promise(resolve => {
    const input = document.createElement('input')
    input.type = 'file'
    input.multiple = false
    input.accept = 'text/csv'
    input.onchange = () => resolve(input.files?.length ? input.files[0] : null)
    input.click()
  })
}
</script>

<template>
  <div id="drag-target" @dragover.prevent @drop.prevent="dropCsv($event)">
    <PageHeader />

    <div class="main-wrapper">
      <div class="container main">
        <div class="config-wrapper">
          <div class="section-nav-wrapper">
            <div ref="nav" class="section-nav card">
              <div class="section-nav__header">
                Translation for<br>
                {{ brand }} / {{ locale }}
              </div>
              <div class="section-nav__navigation">
                <div id="section-nav-marker" class="section-nav__marker" />
                <ul>
                  <li
                    v-for="(section, index) in sections"
                    :id="'section-nav-' + section"
                    :key="'section' + index"
                    @click="sectionNavItemClicked(section)"
                  >
                    {{ section }}
                  </li>
                </ul>
              </div>
            </div>
          </div>
          <div class="config-sections-container translation">
            <div class="form">
              <h2 class="card__title">
                Port translations
              </h2>
              <div class="card firstCard">
                <div class="form__section">
                  <div class="form__section-inner">
                    <p class="form__description">
                      Export all translations to a CSV file. Import a translated CSV file via Import button or drag and
                      drop it into the browser window.
                    </p>
                    <div class="flex">
                      <button id="btn-import" class="btn is--secondary" @click="importClicked">
                        Import from CSV
                      </button>
                      <button id="btn-export" class="btn is--secondary ml-1" @click="exportClicked">
                        Export to CSV
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <section>
                <h2 class="card__title">
                  Add a new translation
                </h2>
                <div class="card">
                  <div class="form__section">
                    <div class="form__section-inner">
                      <div class="row form__row">
                        <FormElement
                          v-validate="'required|translationKey'"
                          :error="validator['addform:newKey']?.error"
                          class="col-md-6"
                          data-validate-namespace="addform"
                          label="Key"
                        >
                          <input
                            id="new-key"
                            v-model="newKey"
                            autocomplete="off"
                            list="key-prefixes"
                            placeholder="Type a key"
                            type="text"
                          >
                        </FormElement>
                        <FormElement
                          v-validate="'required'"
                          :error="validator['addform:newValue']?.error"
                          class="col-md-6"
                          data-validate-namespace="addform"
                          label="Value"
                        >
                          <input id="new-value" v-model="newValue" placeholder="Type a value" type="text">
                        </FormElement>
                        <div class="col-md-12">
                          <button id="btn-add-translation" class="btn is--secondary is--right" @click="addBtnClicked">
                            Add to translation
                          </button>
                        </div>
                        <datalist v-if="!isReference" id="key-prefixes">
                          <option v-for="val in EDITABLE_KEY_PREFIXES" :key="val">
                            {{ val }}
                          </option>
                        </datalist>
                      </div>
                    </div>
                  </div>
                </div>
              </section>
              <h2 class="card__title">
                Filter translations
              </h2>
              <div class="card firstCard">
                <div class="form__section">
                  <div class="form__section-inner">
                    <div class="row form__row flex">
                      <div class="col-12">
                        <FormElement>
                          <label class="form__checkbox">
                            <input v-model="filterUntranslated" type="checkbox" @change="updateTranslation">
                            <span class="form__cm" />
                            Show only untranslated items
                          </label>
                        </FormElement>
                      </div>
                      <div class="col-12">
                        <FormElement class="form__element is--search listing__search">
                          <input
                            id="key-filter"
                            v-model="filterValue"
                            placeholder="Filter translations"
                            type="search"
                            @change="updateTranslation"
                            @input="updateTranslation"
                          >
                        </FormElement>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div class="form">
              <div class="card">
                <div
                  v-for="(section, index) in sections"
                  :id="'section-' + section"
                  :key="'section' + index"
                  :data-section="section"
                  class="form__section has--line"
                >
                  <div class="form__section-inner">
                    <h2 class="form__title">
                      {{ section }}
                    </h2>

                    <div
                      v-for="(item, key) in flatTranslation[section]"
                      :id="convertToKey(section, key)"
                      :key="key"
                      class="row form__row translation__row"
                    >
                      <div :title="key" class="col-12 translation__label">
                        {{ section + '.' + key }}
                        <button
                          class="icon-btn translation__delete-btn"
                          @click="deleteBtnClicked(item.transObj, item.transKey, `${section}.${key}`)"
                        >
                          <span>Delete</span><i class="ic-trash" />
                        </button>
                      </div>
                      <div class="col-lg-6 form__element" @click.shift="copyRefText(item)">
                        <span class="translation__ref">{{ item.ref }}</span>
                      </div>
                      <div class="col-lg-6 form__element">
                        <textarea
                          :id="section + '.' + key"
                          v-model="item.transObj[item.transKey]"
                          class="translation__textarea"
                          rows="1"
                          @input="resizeTextarea($event.currentTarget)"
                        />
                        <p :title="getError(section + '.' + key)" class="form__error">
                          {{ getError(section + '.' + key) }}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="config__controls config-controls">
        <div class="container">
          <button class="btn is--secondary config-controls__reset" @click="resetBtnClicked">
            Reset changes
          </button>
          <button class="btn config-controls__save" @click.shift="fillEmptyFields" @click.exact="saveBtnClicked">
            Save changes
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.translation {
  .form__element.is--search {
    width: 100%;
    padding-right: 0.75rem;
  }

  &__label {
    margin-bottom: rem-calc(16);
    display: flex;
  }

  &__row {
    margin: rem-calc(16) 0;
    border-bottom: 1px solid $borderColorLight;

    &:last-child {
      border-bottom: none;
    }
  }

  &__delete-btn {
    margin-left: auto;

    @include hoverChildFadeIn(0.3s, 'span');

    i[class^='ic-'],
    i[class*=' ic-'] {
      font-size: rem-calc(28);
      width: rem-calc(20);
    }
  }

  &__textarea {
    width: 100%;
    font-size: rem-calc($textMidSize);
    padding: rem-calc(7) rem-calc(12);
    resize: vertical;
    overflow: hidden;
    min-height: 1em;
    height: auto;
  }

  &__ref {
    opacity: 0.5;
    line-height: normal;
    font-size: rem-calc($textMidSize);
    display: inline-block;
    margin-top: rem-calc(8);
    padding-right: rem-calc(26);
  }
}

.ml-1 {
  margin-left: rem-calc(16);
}
</style>

<style lang="scss" scoped>
.firstCard {
  & .form__element {
    margin-bottom: 0;
  }
}

.form {
  .card {
    overflow: visible;
    margin-bottom: rem-calc(32);
  }
}
</style>
